import {
  Cake,
  CalendarDays,
  Camera,
  ChefHat,
  Home,
  ListChecks,
  MessageCircleHeart,
  ShoppingBag,
  Sparkles,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { formatTime, shortDate, weekdayName } from '@awwyeah/shared';
import { useUser } from '../auth';
import { CalendarSection } from '../calendar';
import { PinnedTrackers } from '../trackers';
import { useArrivalFireworks } from '../workSchedule';
import { Fireworks } from '../../components/Fireworks';
import { DigestPrefControl } from './DigestPrefControl';
import { DIGEST_SECTION_CAP, MoreLink } from './MoreLink';
import { findGroceriesList, GroceriesCard } from './GroceriesCard';
import { NEUTRAL_STYLE, useMembers, type DigestMember } from './members';
import { effectiveDigestTime, useComposedDigest, useDigestSources } from './useDigestData';

/**
 * The daily digest (WS-14, ROADMAP §2) — "one glance at breakfast": today's agenda, your
 * open tasks, freshly-added shopping items, yesterday's/overnight check-ins and birthdays
 * ≤7 days out, one screen, no navigation required. It's also where the push notification
 * deep-links to, since iOS pushes are text-only and this view carries the richness.
 *
 * Pure read: everything rendered here comes from `useDigestSources()` +
 * `composeDigest()` (`@awwyeah/shared`), never a write back to any doc except the one
 * explicit preference control at the top.
 */
export function DigestPage(): JSX.Element {
  const { user } = useUser();
  const members = useMembers();
  // WS-15: my own private calendar + list registry feed my digest too.
  const sources = useDigestSources(user?.id ?? null);
  const now = new Date();
  const digest = useComposedDigest(sources, user?.id ?? null, now);

  if (!user || !digest) return <>{sources.listItemBridges}</>;

  const myTime = effectiveDigestTime(sources.digestPrefs, user.id);
  const arrival = useArrivalFireworks(user.id);
  const totalThings =
    digest.today.events.length +
    digest.tasks.forYou.length +
    digest.newListItems.items.length +
    digest.checkins.items.length +
    digest.birthdays.items.length;

  // A brand-new household has nothing in *any* of its docs yet, not just "nothing today" —
  // that's the signal to show the welcome/orientation card instead of the "quiet one" card,
  // which would otherwise read like a bug ("why is nothing here?") to a first-time member.
  const isBrandNew =
    sources.events.length === 0 &&
    sources.lists.length === 0 &&
    sources.checkins.length === 0 &&
    sources.birthdays.length === 0;

  // Pinned below the agenda (Dan, 2026-08-07): agenda first, groceries right after.
  const groceries = findGroceriesList(sources.lists);

  return (
    <div className="flex flex-col gap-6 py-4" data-testid="digest-page">
      {/* Mounted once, invisibly — keeps `itemsByListId` live for every list. */}
      {sources.listItemBridges}

      <header className="flex flex-col gap-2">
        <h1 className="font-display text-2xl tracking-tight text-fg" data-testid="digest-greeting">
          Good morning, {user.name}
        </h1>
        <p className="text-sm text-fg-3">
          {weekdayName(now)}, {shortDate(now, now)}
        </p>
        <DigestPrefControl memberId={user.id} currentTime={myTime} map={sources.digestPrefsMap} />
      </header>

      {/* Only for members with a work-schedule profile — Kora's one-tap sheet upload, and a
          little burst of fireworks the first time she lands here each session. */}
      {arrival.show && <Fireworks size="small" onDone={arrival.dismiss} />}

      {isBrandNew ? (
        <WelcomeCard />
      ) : totalThings === 0 ? (
        <div className="flex flex-col items-start gap-2 rounded-md border border-line bg-surface px-4 py-6">
          <Sparkles className="h-5 w-5 text-accent" aria-hidden="true" />
          <p className="font-display text-lg text-fg">A quiet one</p>
          <p className="text-sm text-fg-2">
            Nothing on today's agenda, nothing new on the lists, nobody checked in overnight. Enjoy
            the calm.
          </p>
        </div>
      ) : (
        <p className="text-sm font-semibold text-fg-2" data-testid="digest-summary">
          {digest.summary}
        </p>
      )}

      {!isBrandNew && (
        <>
          {/* The month, owned and rendered by the calendar module (Dan, 2026-08-18). */}
          <CalendarSection />

          <DigestSection
            title="Today"
            icon={<CalendarDays className="h-4 w-4 text-accent" aria-hidden="true" />}
            testId="digest-section-today"
          >
            {digest.today.events.length === 0 ? (
              <EmptyRow>Nothing on the calendar today.</EmptyRow>
            ) : (
              <ul className="flex flex-col gap-2">
                {digest.today.events.slice(0, DIGEST_SECTION_CAP).map((event) => {
                  const ownerId = event.ownerIds[0];
                  const owner = ownerId ? members.get(ownerId) : null;
                  const style = owner ? owner.style : NEUTRAL_STYLE;
                  return (
                    <li key={event.key} data-testid="digest-event">
                      <Link
                        to="/calendar"
                        style={style.edge}
                        className="flex items-center gap-3 rounded-sm border-l-4 bg-surface px-3 py-2.5 transition-colors hover:bg-raised"
                      >
                        <MemberDot member={owner} />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-fg">{event.title}</p>
                          <p className="text-xs text-fg-3">
                            {event.allDay ? 'All day' : formatTime(event.start)}
                            {event.location ? ` · ${event.location}` : ''}
                          </p>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
            <MoreLink
              hidden={digest.today.events.length - DIGEST_SECTION_CAP}
              to="/calendar"
              testId="digest-more-events"
            />
          </DigestSection>

          {/* Pinned trackers sit right under the agenda: "has anyone fed the dog" is a
              breakfast question, and this is the breakfast screen. Renders nothing when
              nothing is pinned (Dan, 2026-08-18). */}
          <PinnedTrackers />

          {groceries && <GroceriesCard list={groceries} userId={user.id} />}

          <DigestSection
            title="For you"
            icon={<ListChecks className="h-4 w-4 text-accent" aria-hidden="true" />}
            testId="digest-section-tasks"
          >
            {digest.tasks.forYou.length === 0 ? (
              <EmptyRow>No open tasks assigned to you.</EmptyRow>
            ) : (
              <ul className="flex flex-col overflow-hidden rounded-sm border border-line">
                {digest.tasks.forYou.slice(0, DIGEST_SECTION_CAP).map((task) => (
                  <li
                    key={task.itemId}
                    className="border-b border-line last:border-b-0"
                    data-testid="digest-task"
                  >
                    <Link
                      to={`/lists/${task.listId}`}
                      className="flex items-center gap-2 bg-surface px-3 py-2.5 text-sm transition-colors hover:bg-raised"
                    >
                      <span className="truncate text-fg">{task.text}</span>
                      <span className="ml-auto shrink-0 text-xs text-fg-3">
                        {task.listEmoji ?? ''} {task.listName}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            <MoreLink
              hidden={digest.tasks.forYou.length - DIGEST_SECTION_CAP}
              to="/lists"
              label="for you"
              testId="digest-more-tasks"
            />
          </DigestSection>

          <DigestSection
            title="New on the lists"
            icon={<ShoppingBag className="h-4 w-4 text-accent" aria-hidden="true" />}
            testId="digest-section-new-items"
          >
            {digest.newListItems.items.length === 0 ? (
              <EmptyRow>Nothing added in the last day.</EmptyRow>
            ) : (
              <ul className="flex flex-col gap-1.5">
                {digest.newListItems.items.slice(0, DIGEST_SECTION_CAP).map((item) => {
                  const adder = members.get(item.addedBy);
                  return (
                    <li key={item.itemId} data-testid="digest-new-item">
                      <Link
                        to={`/lists/${item.listId}`}
                        className="-mx-1.5 flex items-center gap-2 rounded-sm px-1.5 py-1 text-sm transition-colors hover:bg-raised"
                      >
                        <MemberDot member={adder} size="sm" />
                        <span className="truncate text-fg-2">
                          <span className="font-semibold text-fg">{adder.name}</span> added{' '}
                          <span className="text-fg">{item.text}</span>
                        </span>
                        <span className="ml-auto shrink-0 text-xs text-fg-3">{item.listName}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
            <MoreLink
              hidden={digest.newListItems.items.length - DIGEST_SECTION_CAP}
              to="/lists"
              label="new"
              testId="digest-more-new-items"
            />
          </DigestSection>

          <DigestSection
            title="Check-ins"
            icon={<MessageCircleHeart className="h-4 w-4 text-accent" aria-hidden="true" />}
            testId="digest-section-checkins"
          >
            {digest.checkins.items.length === 0 ? (
              <EmptyRow>Quiet overnight — no new check-ins.</EmptyRow>
            ) : (
              <ul className="flex flex-col gap-2">
                {digest.checkins.items.map((checkin) => {
                  const author = members.get(checkin.authorId);
                  return (
                    <li key={checkin.id} data-testid="digest-checkin">
                      <Link
                        to={`/checkin/p/${checkin.id}`}
                        className="flex items-start gap-2.5 rounded-sm border border-line bg-surface px-3 py-2.5 text-sm transition-colors hover:bg-raised"
                      >
                        <MemberDot member={author} />
                        <div className="min-w-0 flex-1">
                          <p className="text-fg">
                            <span className="font-semibold">{author.name}</span>
                            {checkin.hasPhoto ? ' shared a photo' : ' checked in'}
                          </p>
                          {checkin.text && <p className="truncate text-fg-2">“{checkin.text}”</p>}
                          {checkin.placeName && (
                            <p className="text-xs text-fg-3">{checkin.placeName}</p>
                          )}
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </DigestSection>

          {digest.birthdays.items.length > 0 && (
            <DigestSection
              title="Coming up"
              icon={<Cake className="h-4 w-4 text-accent" aria-hidden="true" />}
              testId="digest-section-birthdays"
            >
              <ul className="flex flex-col gap-1.5">
                {digest.birthdays.items.map((birthday) => (
                  <li key={birthday.id} data-testid="digest-birthday">
                    <Link
                      to="/household"
                      className="-mx-1.5 flex items-center justify-between rounded-sm px-1.5 py-1 text-sm transition-colors hover:bg-raised"
                    >
                      <span className="text-fg">
                        {birthday.turningAge === null
                          ? `${birthday.name}'s birthday`
                          : `${birthday.name} turns ${birthday.turningAge}`}
                      </span>
                      <span className="text-xs text-fg-3">
                        {birthday.daysUntil === 0
                          ? 'Today'
                          : birthday.daysUntil === 1
                            ? 'Tomorrow'
                            : `${birthday.daysUntil}d`}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </DigestSection>
          )}
        </>
      )}
    </div>
  );
}

/**
 * First-run orientation for a brand-new household (no calendar events, lists, check-ins
 * or birthdays yet anywhere — not just "nothing today"). Replaces the whole digest body
 * (not just the "quiet one" card) so the very first screen a new member sees is one
 * instructive glance at what each tab is for, not five empty sections that read like
 * something's broken.
 */
function WelcomeCard(): JSX.Element {
  return (
    <div className="flex flex-col gap-4 rounded-md border border-line bg-surface px-4 py-6">
      <div className="flex flex-col items-start gap-2">
        <Sparkles className="h-5 w-5 text-accent" aria-hidden="true" />
        <p className="font-display text-lg text-fg">Welcome home</p>
        <p className="text-sm text-fg-2">
          This is your household's daily digest — it'll fill in as your family uses the app. Here's
          what lives in each tab:
        </p>
      </div>
      <ul className="flex flex-col gap-1">
        <WelcomeLink
          to="/calendar"
          icon={<CalendarDays className="h-4 w-4 text-accent" aria-hidden="true" />}
          label="Calendar"
          description="Shared events, everyone's schedule at a glance."
        />
        <WelcomeLink
          to="/lists"
          icon={<ShoppingBag className="h-4 w-4 text-accent" aria-hidden="true" />}
          label="Lists"
          description="Shopping and to-do lists the whole house can edit."
        />
        <WelcomeLink
          to="/checkin"
          icon={<Camera className="h-4 w-4 text-accent" aria-hidden="true" />}
          label="Check-in"
          description="Quick photo updates for the family."
        />
        <WelcomeLink
          to="/recipes"
          icon={<ChefHat className="h-4 w-4 text-accent" aria-hidden="true" />}
          label="Recipes"
          description="A shared recipe box that builds shopping lists."
        />
        <WelcomeLink
          to="/household"
          icon={<Home className="h-4 w-4 text-accent" aria-hidden="true" />}
          label="House"
          description="Members, birthdays and household settings."
        />
      </ul>
    </div>
  );
}

function WelcomeLink({
  to,
  icon,
  label,
  description,
}: {
  to: string;
  icon: JSX.Element;
  label: string;
  description: string;
}): JSX.Element {
  return (
    <li>
      <Link
        to={to}
        className="flex min-h-11 items-center gap-3 rounded-sm px-2 py-2 transition-colors hover:bg-raised"
      >
        {icon}
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-fg">{label}</span>
          <span className="block truncate text-xs text-fg-3">{description}</span>
        </span>
      </Link>
    </li>
  );
}

function DigestSection({
  title,
  icon,
  testId,
  children,
}: {
  title: string;
  icon: JSX.Element;
  testId: string;
  /** A list (or empty row) plus, where the section caps, its `MoreLink`. */
  children: ReactNode;
}): JSX.Element {
  return (
    <section className="flex flex-col gap-2" data-testid={testId}>
      <div className="flex items-center gap-2">
        {icon}
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-fg-3">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function EmptyRow({ children }: { children: string }): JSX.Element {
  return (
    <p className="rounded-sm border border-dashed border-line px-3 py-2.5 text-sm text-fg-3">
      {children}
    </p>
  );
}

function MemberDot({
  member,
  size = 'md',
}: {
  member: DigestMember | null;
  size?: 'sm' | 'md';
}): JSX.Element {
  const sizeClass = size === 'sm' ? 'h-5 w-5 text-[10px]' : 'h-7 w-7 text-[11px]';
  const style = member ? member.style : NEUTRAL_STYLE;
  return (
    <span
      title={member?.name ?? 'Unknown member'}
      style={style.fill}
      className={`inline-flex shrink-0 items-center justify-center rounded-full font-bold ${sizeClass}`}
    >
      {member?.avatarEmoji ?? member?.initial ?? '?'}
    </span>
  );
}
